/**
 * Contratti Zod per la scelta embedder del tenant.
 *
 * L'embedder (provider + modello) si sceglie UNA volta con il lock
 * (`POST /tenant/embedder-status/lock`) e non cambia più: il PUT delle
 * impostazioni non accetta provider né modello embedder.
 */
import { z } from 'zod';
import { EMBEDDER_CAPABILITIES, type EmbedderProvider } from '../embedder-capabilities';

export const EmbedderProviderSchema = z.enum(['openai', 'gemini']);

/** Modello embedder ufficiale del provider (unico supportato). */
export function embedderModelFor(provider: EmbedderProvider): string {
  return EMBEDDER_CAPABILITIES[provider].model;
}

/**
 * Body `POST /tenant/embedder-status/lock`. `model`, se presente, deve essere
 * il modello ufficiale del provider: nessun modello arbitrario viene lockato.
 */
export const EmbedderLockInputSchema = z
  .object({
    provider: EmbedderProviderSchema,
    model: z.string().trim().min(1).optional(),
    confirm: z.literal(true),
  })
  .strict()
  .refine((v) => v.model === undefined || v.model === embedderModelFor(v.provider), {
    message: 'model deve essere il modello embedder ufficiale del provider scelto',
    path: ['model'],
  });

export type EmbedderLockInput = z.infer<typeof EmbedderLockInputSchema>;

const NullableModelIdSchema = z.string().trim().min(1).max(128).nullable();

/**
 * Body `PUT /tenant/embedder-status`: solo le impostazioni modificabili dopo
 * il lock. Provider e modello embedder sono esclusi (`strict` → 400).
 */
export const EmbedderSettingsUpdateSchema = z
  .object({
    image_chunks_enabled: z.boolean().optional(),
    gemini_chat_model: NullableModelIdSchema.optional(),
    openai_chat_model: NullableModelIdSchema.optional(),
    wiki_scrape_index_media: z.boolean().optional(),
    expected_revision: z.number().int().nonnegative().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).some((key) => key !== 'expected_revision'), { message: 'Nessun campo da aggiornare' });

export type EmbedderSettingsUpdate = z.infer<typeof EmbedderSettingsUpdateSchema>;

/**
 * Body `POST /tenant/ai-preset/apply`: `fill` completa la configurazione
 * consigliata senza toccare le scelte del tenant; `reset` riporta tutte le
 * funzioni coperte dal preset alla configurazione consigliata.
 */
export const EmbedderPresetApplyInputSchema = z
  .object({ mode: z.enum(['fill', 'reset']) })
  .strict();

export type EmbedderPresetApplyInput = z.infer<typeof EmbedderPresetApplyInputSchema>;
